/**
 * Layar histogram.
 *
 * Sebuah histogram pernah ada di aplikasi ini: kotak setinggi sembilan puluh
 * enam piksel di sudut layar crossplot, empat belas bin yang tidak bisa
 * diubah, kurvanya terkunci pada PHIE, dan satu angka rata-rata di bawahnya.
 * Itu cukup untuk memastikan sebuah kurva tidak kosong, dan tidak cukup untuk
 * satu pun pertanyaan yang benar-benar dibawa orang ke sebuah histogram:
 * berapa porositas yang pantas dipakai model, apakah zona B memang lebih baik
 * daripada A, apakah sumur yang satu ini memang berbeda atau hanya kelihatan
 * berbeda.
 *
 * Ketiganya pertanyaan tentang *sebaran* dan bukan tentang rata-rata, dan
 * ketiganya menuntut hal yang sama: bin yang bisa disetel, sumbu yang bisa
 * dikunci, dan kemampuan memecah satu sebaran menjadi beberapa yang berdiri di
 * atas sumbu yang sama. Rata-rata adalah jawaban yang paling mudah dipercaya
 * dan paling sering salah — porositas yang menjulur ke kanan dilaporkan
 * lengkap oleh p50 yang jauh di bawahnya, dan tidak dilaporkan sama sekali
 * oleh rata-rata sendirian. Karena itu tabel di sini menyebut p10, p50 dan p90,
 * bukan rata-rata saja.
 */

import { useMemo } from "react";

import { api } from "../api";
import {
  DataScope,
  DEFAULT_SCOPE,
  normalizeScope,
  scopeParams,
  ScopeEmpty,
} from "../components/DataScope";
import {
  HistogramChart,
  HistogramLegend,
  type HistogramLayout,
  type HistogramMode,
} from "../components/HistogramChart";
import { RailCollapseButton, RailStrip, type RailTabItem } from "../components/RailTabs";
import { Resizer } from "../components/Resizer";
import {
  Dropdown,
  Empty,
  Loading,
  Notice,
  OptionalNumberField,
  Panel,
  Segmented,
  Slider,
  Toggle,
} from "../components/ui";
import { useAsync, useStored, useWellSync } from "../hooks";
import {
  adaptOne,
  curveOptions,
  useAdaptOnNewScope,
  useLastDefined,
  usePreferredCurve,
} from "../lib/curveoptions";
import { distinctColors } from "../lib/xplot";
import type { FieldHistogram, Project, Well, XplotStats } from "../types";

const RAIL_TABS: RailTabItem<"histogram">[] = [{ key: "histogram", label: "Histogram" }];

type GroupBy = "none" | "well" | "zone";

export default function Histogram({
  project,
  well,
  wells,
  dataVersion = 0,
  onWellChange,
}: {
  project: Project;
  well: Well;
  wells: Well[];
  dataVersion?: number;
  /**
   * Dipanggil ketika lingkup layar ini menyempit menjadi satu sumur, supaya
   * pemilih WELL di cangkang menunjuk sumur yang sama. Lihat `useWellSync`.
   */
  onWellChange?: (wellId: number) => void;
}) {
  const [railWidth, setRailWidth] = useStored("evolog.hist.rail", 300);
  const [railCollapsed, setRailCollapsed] = useStored("evolog.hist.rail.collapsed", false);
  // Lingkup tersimpan dibaca lewat `normalizeScope`: bentuk lama yang masih
  // ada di peramban orang menyebut selang markernya dengan kata-kata yang
  // sudah tidak dipakai lagi, dan yang dibiarkan apa adanya berangkat sebagai
  // kueri tanpa selang sama sekali.
  const [storedScope, setScope] = useStored<unknown>(`evolog.hist.scope.${project.id}`, {
    ...DEFAULT_SCOPE,
    wells: [well.id],
  });
  const scope = useMemo(() => normalizeScope(storedScope), [storedScope]);

  /* Sama dengan crossplot: satu sumur di dalam lingkup berarti layar ini
     berdiri di atas sumur itu. Lingkup kosong berarti seluruh lapangan, dan
     seluruh lapangan bukan sebuah sumur. */
  useWellSync(scope.wells.length === 1 ? scope.wells[0] : null, well.id, {
    onFocus: onWellChange,
    onAdopt: (wellId) => setScope({ ...scope, wells: [wellId] }),
  });

  const chosen = usePreferredCurve(useStored("evolog.hist.curve", "PHIE"));
  const [curve, setCurve] = [chosen.value, chosen.choose];
  const [bins, setBins] = useStored("evolog.hist.bins", 24);
  const [min, setMin] = useStored<number | null>("evolog.hist.min", null);
  const [max, setMax] = useStored<number | null>("evolog.hist.max", null);
  const [log, setLog] = useStored("evolog.hist.log", false);
  const [group, setGroup] = useStored<GroupBy>("evolog.hist.group", "none");
  const [mode, setMode] = useStored<HistogramMode>("evolog.hist.mode", "count");
  const [layout, setLayout] = useStored<HistogramLayout>("evolog.hist.layout", "overlay");
  const [cumulative, setCumulative] = useStored("evolog.hist.cum", false);

  const data = useAsync(
    (signal) =>
      api.get<FieldHistogram>(
        `/api/projects/${project.id}/histogram/`,
        {
          curve,
          bins,
          group,
          log: log ? "1" : undefined,
          min: min ?? undefined,
          max: max ?? undefined,
          ...scopeParams(scope),
        },
        signal,
      ),
    [project.id, curve, bins, group, log, min, max, scope, dataVersion],
  );

  const projectCurves = useLastDefined(data.data?.curves) ?? data.data?.available ?? [];
  const options = useMemo(
    () => curveOptions(projectCurves, data.data?.available, [curve]),
    [projectCurves, data.data?.available, curve],
  );
  // Kurva mengikuti sumur, seperti sumbu crossplot (lib/curveoptions.ts).
  useAdaptOnNewScope(data.data, data.loading, JSON.stringify(scope), () => {
    chosen.use(adaptOne(data.data?.available ?? [], chosen.preferred, ["PHIE", "PHIT", "GR"])
               ?? chosen.preferred);
  });

  // Kelompok per sumur mewarisi warna status sumurnya, dan status itu berulang:
  // tiga sumur minyak akan menjadi tiga sebaran berwarna hijau yang sama pada
  // satu gambar yang justru dibuat untuk membedakannya.
  const shown = useMemo(() => {
    const payload = data.data;
    if (!payload || payload.group !== "well") return payload;
    const colors = distinctColors(payload.groups.map((group) => group.color));
    return { ...payload, groups: payload.groups.map((group, index) => ({ ...group, color: colors[index] })) };
  }, [data.data]);

  return (
    <div style={{ display: "flex", alignItems: "stretch", flex: 1, minHeight: 0 }}>
      <section
        style={{
          flex: 1,
          minWidth: 0,
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
          <span style={{ font: "600 11px var(--font-sans)", color: "var(--ink)" }}>
            Histogram {curve}
          </span>
          <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
            {data.data?.wells?.length ?? 0} wells
            {group === "well" ? " · split per well" : ""}
            {group === "zone" ? " · split per zone" : ""}
            {log ? " · logarithmic bins" : ""}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
            {data.data?.total ? `${data.data.total.n.toLocaleString("en-US")} samples` : ""}
          </span>
        </div>

        {data.loading && !data.data && <Loading label="Computing distribution…" />}
        {data.error && <Notice tone="error">{data.error}</Notice>}
        {shown && !shown.total && <ScopeEmpty skipped={data.data?.skipped} />}
        {shown && shown.total && (
          <>
            <HistogramChart
              data={shown}
              mode={mode}
              layout={layout}
              showCumulative={cumulative}
              showAxisLabel
            />
            <HistogramLegend data={shown} />
            <StatsTable data={shown} />
          </>
        )}
      </section>

      {!railCollapsed && (
        <Resizer
          width={railWidth}
          onChange={setRailWidth}
          min={250}
          max={560}
          label="Resize the histogram panel"
        />
      )}
      {railCollapsed ? (
        <RailStrip
          tabs={RAIL_TABS}
          active="histogram"
          onSelect={() => setRailCollapsed(false)}
          expandLabel="Show the histogram panel"
        />
      ) : (
        <aside
          style={{
            width: railWidth,
            flex: "none",
            background: "var(--rail)",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "4px 7px 4px 13px",
              borderBottom: "1px solid var(--line-soft)",
              flex: "none",
            }}
          >
            <span className="label">Histogram</span>
            <RailCollapseButton
              side="right"
              collapsed={false}
              label="Hide the histogram panel"
              onClick={() => setRailCollapsed(true)}
            />
          </div>
          <div
            className="scroll-y"
            style={{ flex: 1, padding: "14px 13px", display: "flex", flexDirection: "column", gap: 12 }}
          >
            <Panel title="Curve & bins">
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>CURVE</span>
                <Dropdown
                  value={curve}
                  options={options.length ? options : [{ value: curve, label: curve }]}
                  onChange={setCurve}
                  width="100%"
                />
              </div>
              <Slider
                label="Bin count"
                value={bins}
                min={4}
                max={80}
                step={1}
                onChange={setBins}
                format={(v) => v.toFixed(0)}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <OptionalNumberField label="MIN" value={min} onChange={setMin} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <OptionalNumberField label="MAX" value={max} onChange={setMax} />
                </div>
              </div>
              <Toggle label="Logarithmic bins" checked={log} onChange={setLog} />
              {/* Permeabilitas membentang empat besaran; bin selebar sama pada
                  sumbu linier akan menaruh sembilan persepuluh sampelnya di
                  dalam satu batang pertama. */}
              <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                A bound left blank follows the range of its own data. Locking it is the only
                way to compare two different scopes.
              </span>
            </Panel>

            <DataScope
              wells={wells}
              scope={scope}
              onChange={setScope}
              skipped={data.data?.skipped}
              screening={data.data?.screening}
            />

            <Panel title="Grouping">
              <Segmented
                value={group}
                size="sm"
                options={[
                  { value: "none", label: "All in one" },
                  { value: "well", label: "Per well" },
                  { value: "zone", label: "Per zone" },
                ]}
                onChange={(value) => setGroup(value as GroupBy)}
              />
              {group !== "none" && (
                <Segmented
                  value={layout}
                  size="sm"
                  options={[
                    { value: "overlay", label: "Overlaid" },
                    { value: "stack", label: "Stacked" },
                  ]}
                  onChange={(value) => setLayout(value as HistogramLayout)}
                />
              )}
              {group === "zone" && (
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                  Every sample is split by the zone that really holds it, with no need to bound
                  the interval first. Depths inside no zone at all gather as "no zone".
                </span>
              )}
            </Panel>

            <Panel title="Vertical axis">
              <Segmented
                value={mode}
                size="sm"
                options={[
                  { value: "count", label: "Count" },
                  { value: "percent", label: "Per cent" },
                  { value: "cumulative", label: "Cumulative" },
                ]}
                onChange={(value) => setMode(value as HistogramMode)}
              />
              <Toggle
                label="Cumulative line"
                checked={cumulative}
                onChange={setCumulative}
                disabled={mode === "cumulative"}
              />
              {/* Persen menjawab "seperti apa bentuknya", cacah menjawab
                  "berapa banyak" — dan dua kelompok yang berbeda sepuluh kali
                  lipat besarnya terlihat kembar pada persen. */}
              <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                Per cent compares shape; counts compare size. Both are needed, and both mislead
                when read as the other.
              </span>
            </Panel>
          </div>
        </aside>
      )}
    </div>
  );
}

/**
 * Tabel statistik, satu baris per kelompok.
 *
 * Persentil dan bukan rata-rata sendirian: sebuah sebaran yang menjulur
 * dilaporkan salah oleh rata-rata, dan justru sebaran seperti itulah yang
 * paling sering dipakai memutuskan cutoff.
 */
function StatsTable({ data }: { data: FieldHistogram }) {
  const rows = data.groups.filter((group) => group.stats);
  if (!rows.length) return <Empty>No sample to summarise yet.</Empty>;

  const columns: { key: keyof XplotStats; label: string }[] = [
    { key: "n", label: "n" },
    { key: "mean", label: "mean" },
    { key: "sd", label: "sd" },
    { key: "min", label: "min" },
    { key: "p10", label: "p10" },
    { key: "p50", label: "p50" },
    { key: "p90", label: "p90" },
    { key: "max", label: "max" },
  ];

  const cell = {
    padding: "5px 8px",
    font: "400 10.5px var(--font-mono)",
    color: "var(--ink-2)",
    textAlign: "right" as const,
    whiteSpace: "nowrap" as const,
  };

  return (
    <div
      className="scroll-y"
      style={{
        flex: "none",
        maxHeight: 190,
        border: "1px solid var(--line-strong)",
        borderRadius: "var(--radius-panel)",
        background: "var(--panel)",
        overflowX: "auto",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid var(--line)" }}>
            <th style={{ ...cell, textAlign: "left", color: "var(--ink-5)" }}>Kelompok</th>
            {columns.map((column) => (
              <th key={column.key} style={{ ...cell, color: "var(--ink-5)" }}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((group) => (
            <tr key={group.key} style={{ borderTop: "1px solid var(--line-soft)" }}>
              <td style={{ ...cell, textAlign: "left" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: group.color }} />
                  {group.label}
                </span>
              </td>
              {columns.map((column) => (
                <td key={column.key} style={cell}>
                  {column.key === "n"
                    ? group.stats![column.key].toLocaleString("en-US")
                    : group.stats![column.key].toFixed(4)}
                </td>
              ))}
            </tr>
          ))}
          {data.total && rows.length > 1 && (
            <tr style={{ borderTop: "1px solid var(--line)" }}>
              <td style={{ ...cell, textAlign: "left", color: "var(--ink-5)" }}>Seluruhnya</td>
              {columns.map((column) => (
                <td key={column.key} style={{ ...cell, color: "var(--ink-5)" }}>
                  {column.key === "n"
                    ? data.total![column.key].toLocaleString("en-US")
                    : data.total![column.key].toFixed(4)}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
